import { DestinationItem } from '@tableau/nested-project-explorer';
import React from 'react';

import { openReactActionDialog, ReactActionDialogBodyProps } from '~commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { Messages } from '~src/codegen/Localize';
import { DestinationProjectMapper } from '~src/projects/models/mappers/DestinationProjectMapper';
import { ProjectTreeService } from '~src/projects/services/ProjectTreeService';

import { IEditRoutingFilterChanges, IEditRoutingFilterProjectChanges } from '../../../serverApi/requests/EditRoutingFilterRequest';
import { IRoutingFilterError, RoutingFilterResponse } from '../../../serverApi/requests/RoutingFilterTypes';
import { RoutingFilter } from '../../../serverApi/resources/RoutingFilter';
import { DomainValidationStatus } from '../../types/DomainValidationStatus';
import { PoolAssignmentOption } from '../../types/PoolAssignmentOption';
import { PoolID } from '../../types/PoolID';
import { isConflictError } from '../../types/RoutingFilterErrorCode';
import { RoutingFilterPermission } from '../../types/RoutingFilterPermission';
import { validateDomainName } from '../../utils/DomainUtilities';
import { getRoutingFilterErrorMessage } from '../../utils/RoutingFilterErrorUtilities';
import { ConflictError } from '../RoutingFiltersConflictContext';
import { RoutingFilterDetailsDialogBody } from './RoutingFilterDetailsDialogBody';

type ProjectWithLuid = DestinationItem & { luid: string };

const UNSAVED_NEW_FILTER_ID = 'unsaved-new';

export type RoutingFilterDetailsDialogInputProps = Readonly<{
  poolOptions: ReadonlyArray<PoolAssignmentOption>;
  otherDomainNames: ReadonlyArray<string>;
  dataConnectDomainNames: ReadonlyArray<string>;
  initialProjectLuids: ReadonlyArray<string>;
  // Project LUIDs the server flagged as conflicting on the prior save attempt;
  // mapped to tree ids once projects load so the selector can highlight them.
  conflictingProjectLuids: ReadonlyArray<string>;
  // Conflict errors from the prior save attempt, rendered as the dialog's
  // conflict banner. Static input like conflictingProjectLuids — it is seeded
  // once when the dialog reopens and never mutated as the user edits, so it lives
  // in the body props rather than the mutable dialog state.
  conflictErrors: ReadonlyArray<ConflictError>;
  // Clears the filter's deleted-project references (reuses the table action);
  // omitted unless the filter references deleted projects. Resolves true when
  // the clear succeeded so the dialog can drop its warning banner in place.
  onClearDeletedProjects?: () => Promise<boolean>;
}>;

export type RoutingFilterDetailsDialogState = Readonly<{
  domainName: string;
  projectIds: Set<string>;
  poolID: PoolID | null;
  permission: RoutingFilterPermission;
  domainValidationStatus: DomainValidationStatus;
  errorMessage: string;
  projectsLoaded: boolean;
  treeIdToLuidMap: Record<string, string>;
  luidToTreeIdMap: Record<string, string>;
  luidToDisplayNameMap: Record<string, string>;
  // Conflict/warning info from the prior save attempt, shown as banners when the
  // dialog is reopened on an unsaved filter. Cleared as the user edits fields.
  conflictingProjectTreeIds: ReadonlySet<string>;
  showMissingDomainWideWarning: boolean;
  hasDeletedProjects: boolean;
}>;

type RoutingFilterDetailsDialogProps = ReactActionDialogBodyProps<
  RoutingFilterDetailsDialogInputProps,
  RoutingFilterDetailsDialogState
>;

// Exported for unit testing the in-dialog behaviors (e.g. clearing deleted
// projects updates the banner in place). Rendered internally via bodyComponent.
export const RoutingFilterDetailsDialogComponent = (props: RoutingFilterDetailsDialogProps): JSX.Element => {
  const [projects, setProjects] = React.useState<ReadonlyArray<DestinationItem> | null>(null);
  // Guards the in-dialog "Clear Deleted Projects" edit: the button unmounts on
  // success (hasDeletedProjects flips false), but until the request resolves a
  // second click would fire a duplicate editRoutingFilter and success toast. The
  // ref blocks re-entry synchronously (state updates are async, so a fast second
  // click could slip in before re-render); the state drives the button spinner.
  const [isClearDeletedProjectsRequestInFlight, setIsClearDeletedProjectsRequestInFlight] = React.useState(false);
  const clearDeletedProjectsRequestInFlightRef = React.useRef(false);
  const { initialProjectLuids, conflictingProjectLuids, updateDialogState } = props;

  React.useEffect(() => {
    let mounted = true;
    ProjectTreeService.instance.getAllProjects()
      .then(rawProjects => {
        if (!mounted) { return; }
        const mapped = DestinationProjectMapper.map(rawProjects);
        const luidToTreeId: Record<string, string> = {};
        const treeIdToLuid: Record<string, string> = {};
        const luidToDisplayName: Record<string, string> = {};
        for (const p of mapped as ProjectWithLuid[]) {
          if (p.luid) {
            luidToTreeId[p.luid] = p.id;
            treeIdToLuid[p.id] = p.luid;
            luidToDisplayName[p.luid] = p.name;
          }
        }

        const mappedProjectIds = new Set<string>();
        for (const luid of initialProjectLuids) {
          const treeId = luidToTreeId[luid];
          if (treeId) {
            mappedProjectIds.add(treeId);
          }
        }
        // The server reports conflicting projects by LUID; map them to the tree
        // ids the selector renders so the matching rows can be highlighted.
        const conflictingProjectTreeIds = new Set<string>();
        for (const luid of conflictingProjectLuids) {
          const treeId = luidToTreeId[luid];
          if (treeId) {
            conflictingProjectTreeIds.add(treeId);
          }
        }
        updateDialogState({
          projectIds: mappedProjectIds,
          projectsLoaded: true,
          treeIdToLuidMap: treeIdToLuid,
          luidToTreeIdMap: luidToTreeId,
          luidToDisplayNameMap: luidToDisplayName,
          conflictingProjectTreeIds,
        });
        setProjects(mapped);
      })
      .catch(() => {
        if (!mounted) { return; }
        updateDialogState({ errorMessage: Messages.bridgeSiteSettingsRoutingFiltersLoadError(), projectsLoaded: true });
      });
    return () => { mounted = false; };
  }, [initialProjectLuids, conflictingProjectLuids, updateDialogState]);

  // Clears the transient save error on any edit. The missing-domain-wide warning
  // is intentionally NOT cleared here: it is a whole-domain property (the domain
  // lacks a domain-wide rule), so editing the pool, permission, or project
  // selection doesn't invalidate it — only a domain change does (see setDomainName).
  const clearError = () => {
    if (props.dialogState.errorMessage) { props.updateDialogState({ errorMessage: '' }); }
  };
  const setDomainValidationStatus = (status: DomainValidationStatus) => props.updateDialogState({ domainValidationStatus: status });
  const setDomainName = (name: string) => {
    const patch: { domainName: string, errorMessage?: string, showMissingDomainWideWarning?: boolean } = { domainName: name };
    if (props.dialogState.errorMessage) { patch.errorMessage = ''; }
    // A domain change invalidates the prior save attempt's missing-domain-wide
    // warning (it was computed against the old domain), so drop it. Conflict
    // highlights persist until the row is saved again — the body hides the
    // banner once every conflicting project is deselected.
    if (props.dialogState.showMissingDomainWideWarning) { patch.showMissingDomainWideWarning = false; }
    props.updateDialogState(patch);
  };
  const setAssignedPoolID = (poolID: PoolID | null) => { props.updateDialogState({ poolID }); clearError(); };
  const setPermission = (permission: RoutingFilterPermission) => { props.updateDialogState({ permission }); clearError(); };
  const setProjectIds = (ids: Set<string>) => { props.updateDialogState({ projectIds: ids }); clearError(); };

  const onDomainNameChange = (name: string) => {
    setDomainValidationStatus(validateDomainName(name, props.otherDomainNames, props.dataConnectDomainNames));
    setDomainName(name);
  };

  // Clearing deleted-project references is a server edit that doesn't change the
  // selectable projects (deleted projects aren't selectable), so once the API
  // confirms success we just drop the warning banner in place rather than
  // reopening the dialog.
  const { onClearDeletedProjects } = props;
  const clearDeletedProjects = onClearDeletedProjects
    ? () => {
        // Synchronous re-entry guard: a rapid second click before the request
        // resolves would otherwise fire a duplicate edit and success toast.
        if (clearDeletedProjectsRequestInFlightRef.current) { return; }
        clearDeletedProjectsRequestInFlightRef.current = true;
        setIsClearDeletedProjectsRequestInFlight(true);
        onClearDeletedProjects().then(cleared => {
          if (cleared) {
            props.updateDialogState({ hasDeletedProjects: false });
          }
        }).finally(() => {
          clearDeletedProjectsRequestInFlightRef.current = false;
          setIsClearDeletedProjectsRequestInFlight(false);
        });
      }
    : undefined;

  return (
    <RoutingFilterDetailsDialogBody
      onRoutingFilterPermissionChange={setPermission}
      routingFilterPermission={props.dialogState.permission}
      onDomainNameChange={onDomainNameChange}
      domainName={props.dialogState.domainName}
      domainValidationStatus={props.dialogState.domainValidationStatus}
      onProjectIdsChange={setProjectIds}
      projectIds={props.dialogState.projectIds}
      projects={projects}
      onPoolAssignmentChange={setAssignedPoolID}
      assignedPoolID={props.dialogState.poolID}
      poolOptions={props.poolOptions}
      errorMessage={props.dialogState.errorMessage}
      conflictErrors={props.conflictErrors}
      conflictingProjectIds={props.dialogState.conflictingProjectTreeIds}
      showMissingDomainWideWarning={props.dialogState.showMissingDomainWideWarning}
      hasDeletedProjects={props.dialogState.hasDeletedProjects}
      onClearDeletedProjects={clearDeletedProjects}
      isClearDeletedProjectsRequestInFlight={isClearDeletedProjectsRequestInFlight}
    />
  );
};

export function buildProjectChanges(
  originalProjectIds: Set<string>,
  updatedProjectIds: Set<string>,
  treeIdToLuid: Record<string, string>
): IEditRoutingFilterProjectChanges {
  const add: string[] = [];
  const remove: string[] = [];
  updatedProjectIds.forEach(id => {
    if (!originalProjectIds.has(id) && treeIdToLuid[id]) {
      add.push(treeIdToLuid[id]);
    }
  });
  originalProjectIds.forEach(id => {
    if (!updatedProjectIds.has(id) && treeIdToLuid[id]) {
      remove.push(treeIdToLuid[id]);
    }
  });
  return { add, remove };
}

// The nested error shape carried on IRoutingFilterError.errors — kept in sync
// with the request layer so a server field rename fails the build here rather
// than silently dropping conflict highlights.
type RoutingFilterServerError = NonNullable<IRoutingFilterError['errors']>[number];

function mapToConflictErrors(errors: ReadonlyArray<RoutingFilterServerError> | undefined): ConflictError[] {
  if (!errors) {
    return [];
  }
  return errors.map(err => ({
    errorCode: String(err.errorCode ?? ''),
    message: String(err.message ?? ''),
    routingRuleId: err.routingRuleId ? String(err.routingRuleId) : undefined,
    invalidFields: err.invalidFields,
  }));
}

export type OnConflictCallback = (unsavedFilter: RoutingFilterResponse, isNew: boolean, errors: ReadonlyArray<ConflictError>) => void;

export type OpenEditRoutingFilterDialogOptions = Readonly<{
  filterId: string;
  siteId: string;
  domainName: string;
  permission: RoutingFilterPermission;
  poolID: PoolID | null;
  initialProjectLuids: ReadonlyArray<string>;
  savedProjectLuids?: ReadonlyArray<string>;
  poolOptions: ReadonlyArray<PoolAssignmentOption>;
  otherDomainNames: ReadonlyArray<string>;
  dataConnectDomainNames: ReadonlyArray<string>;
  onSuccess: (updatedFilter: RoutingFilterResponse) => void;
  onConflict: OnConflictCallback;
  // Set only when reopening an unsaved (conflicting) filter, to surface the prior
  // save attempt's banners and highlights. Omitted on a normal edit open.
  conflict?: RoutingFilterDialogConflictOptions;
}>;

// Conflict/warning context carried into a reopened routing filter dialog so it
// can reproduce the banners and highlights from the last failed save attempt.
export type RoutingFilterDialogConflictOptions = Readonly<{
  conflictErrors: ReadonlyArray<ConflictError>;
  conflictingProjectLuids: ReadonlyArray<string>;
  showMissingDomainWideWarning: boolean;
  hasDeletedProjects: boolean;
  onClearDeletedProjects?: () => Promise<boolean>;
}>;

async function submitEditRoutingFilter(
  options: OpenEditRoutingFilterDialogOptions,
  dialogState: RoutingFilterDetailsDialogState,
  updateDialogState: (state: Partial<RoutingFilterDetailsDialogState>) => void
) {
  const treeIdToLuid = dialogState.treeIdToLuidMap;
  const luidToTreeId = dialogState.luidToTreeIdMap;
  const baselineLuids = options.savedProjectLuids ?? options.initialProjectLuids;
  const originalProjectIds = new Set<string>();
  for (const luid of baselineLuids) {
    const treeId = luidToTreeId[luid];
    if (treeId) {
      originalProjectIds.add(treeId);
    }
  }

  const isBlocked = dialogState.permission === RoutingFilterPermission.BLOCK;
  const routingFilterChanges: IEditRoutingFilterChanges = {
    domain: dialogState.domainName,
    isBlocked,
    poolId: isBlocked ? undefined : (dialogState.poolID ?? undefined),
    projects: isBlocked
      ? { add: [], remove: Array.from(baselineLuids) }
      : buildProjectChanges(originalProjectIds, dialogState.projectIds, treeIdToLuid),
  };
  const result = await RoutingFilter.editRoutingFilter({
    siteId: options.siteId,
    routingRuleId: options.filterId,
    routingRuleChanges: routingFilterChanges,
  });
  if (result.success) {
    options.onSuccess(result.success.routingRule);
  } else if (isConflictError(result.error?.errorCode)) {
    const luidToDisplayName = dialogState.luidToDisplayNameMap;
    const editProjectLuids = Array.from(dialogState.projectIds).map(id => treeIdToLuid[id] ?? id);
    const unsavedFilter: RoutingFilterResponse = {
      id: options.filterId,
      domain: dialogState.domainName,
      blocked: isBlocked,
      poolId: isBlocked ? undefined : (dialogState.poolID ?? undefined),
      projects: editProjectLuids.map(id => ({ id, displayValue: luidToDisplayName[id] ?? id })),
    };
    options.onConflict(unsavedFilter, false, mapToConflictErrors(result.error?.errors));
    // No throw — returning normally closes the dialog (dismissDialogOnActionFailed: false keeps it open only on throw)
  } else {
    const errorMessage = getRoutingFilterErrorMessage(result.error?.errorCode);
    updateDialogState({ errorMessage });
    throw new Error(errorMessage);
  }
}

export function openEditRoutingFilterDialog(options: OpenEditRoutingFilterDialogOptions) {
  const conflict = options.conflict;
  openReactActionDialog({
    title: Messages.bridgeSiteSettingsEditRoutingFilter(),
    bodyComponent: RoutingFilterDetailsDialogComponent,
    initialBodyProps: {
      poolOptions: options.poolOptions,
      otherDomainNames: options.otherDomainNames,
      dataConnectDomainNames: options.dataConnectDomainNames,
      initialProjectLuids: options.initialProjectLuids,
      conflictingProjectLuids: conflict?.conflictingProjectLuids ?? [],
      conflictErrors: conflict?.conflictErrors ?? [],
      onClearDeletedProjects: conflict?.onClearDeletedProjects,
    },
    initialDialogState: {
      domainName: options.domainName,
      projectIds: new Set<string>(),
      poolID: options.poolID,
      permission: options.permission,
      domainValidationStatus: DomainValidationStatus.NO_ERROR,
      errorMessage: '',
      projectsLoaded: false,
      treeIdToLuidMap: {},
      luidToTreeIdMap: {},
      luidToDisplayNameMap: {},
      conflictingProjectTreeIds: new Set<string>(),
      showMissingDomainWideWarning: conflict?.showMissingDomainWideWarning ?? false,
      hasDeletedProjects: conflict?.hasDeletedProjects ?? false,
    },
    actionDisabled: (_bodyProps, dialogState) => {
      return !dialogState.projectsLoaded
        || dialogState.domainValidationStatus !== DomainValidationStatus.NO_ERROR
        || dialogState.domainName.trim().length === 0
        || (dialogState.permission === RoutingFilterPermission.ALLOW && dialogState.poolID === null);
    },
    actionName: Messages.save(),
    action: async (_bodyProps, dialogState, updateDialogState) => {
      await submitEditRoutingFilter(options, dialogState, updateDialogState);
    },
    isActionDelete: false,
    hideCancelButton: false,
    dialogWidth: TabDialogWidth.width375px,
    dismissDialogOnActionFailed: false,
  });
}

export type CreateRoutingFilterPreFill = Readonly<{
  domainName?: string;
  poolID?: PoolID | null;
  permission?: RoutingFilterPermission;
  projectIds?: Set<string>;
}>;

export type OpenCreateRoutingFilterDialogOptions = Readonly<{
  siteId: string;
  poolOptions: ReadonlyArray<PoolAssignmentOption>;
  otherDomainNames: ReadonlyArray<string>;
  dataConnectDomainNames: ReadonlyArray<string>;
  preFill?: CreateRoutingFilterPreFill;
  initialProjectLuids?: ReadonlyArray<string>;
  onSuccess: (newFilter: RoutingFilterResponse) => void;
  onConflict: OnConflictCallback;
  // Set only when reopening an unsaved (conflicting) new filter, to surface the
  // prior save attempt's banners and highlights. Omitted on a fresh add open.
  conflict?: RoutingFilterDialogConflictOptions;
}>;

async function submitCreateRoutingFilter(
  options: OpenCreateRoutingFilterDialogOptions,
  dialogState: RoutingFilterDetailsDialogState,
  updateDialogState: (state: Partial<RoutingFilterDetailsDialogState>) => void
) {
  const treeIdToLuid = dialogState.treeIdToLuidMap;
  const isBlocked = dialogState.permission === RoutingFilterPermission.BLOCK;
  const projectLuids: string[] = [];

  if (!isBlocked) {
    dialogState.projectIds.forEach(id => {
      if (treeIdToLuid[id]) {
        projectLuids.push(treeIdToLuid[id]);
      }
    });
  }

  if (!isBlocked && dialogState.projectIds.size > 0 && projectLuids.length === 0) {
    updateDialogState({ errorMessage: Messages.bridgeSiteSettingsRoutingFiltersLoadError() });
    throw new Error('Failed to resolve project selections');
  }

  const result = await RoutingFilter.createRoutingFilter({
    siteId: options.siteId,
    routingRule: {
      domain: dialogState.domainName,
      isBlocked,
      poolId: isBlocked ? undefined : (dialogState.poolID ?? undefined),
      projects: projectLuids.length > 0 ? { ids: projectLuids } : undefined,
    },
  });

  if (result.success) {
    options.onSuccess(result.success.routingRule);
  } else if (isConflictError(result.error?.errorCode)) {
    const luidToDisplayName = dialogState.luidToDisplayNameMap;
    const unsavedFilter: RoutingFilterResponse = {
      id: UNSAVED_NEW_FILTER_ID,
      domain: dialogState.domainName,
      blocked: isBlocked,
      poolId: isBlocked ? undefined : (dialogState.poolID ?? undefined),
      projects: projectLuids.map(id => ({ id, displayValue: luidToDisplayName[id] ?? id })),
    };
    options.onConflict(unsavedFilter, true, mapToConflictErrors(result.error?.errors));
    // No throw — returning normally closes the dialog (dismissDialogOnActionFailed: false keeps it open only on throw)
  } else {
    const errorMessage = getRoutingFilterErrorMessage(result.error?.errorCode);
    updateDialogState({ errorMessage });
    throw new Error(errorMessage);
  }
}

export function openCreateRoutingFilterDialog(options: OpenCreateRoutingFilterDialogOptions) {
  const preFill = options.preFill ?? {};
  const conflict = options.conflict;

  openReactActionDialog({
    title: Messages.bridgeSiteSettingsAddRoutingFilter(),
    bodyComponent: RoutingFilterDetailsDialogComponent,
    initialBodyProps: {
      poolOptions: options.poolOptions,
      otherDomainNames: options.otherDomainNames,
      dataConnectDomainNames: options.dataConnectDomainNames,
      initialProjectLuids: options.initialProjectLuids ?? [],
      conflictingProjectLuids: conflict?.conflictingProjectLuids ?? [],
      conflictErrors: conflict?.conflictErrors ?? [],
      onClearDeletedProjects: conflict?.onClearDeletedProjects,
    },
    initialDialogState: {
      domainName: preFill.domainName ?? '',
      projectIds: preFill.projectIds ?? new Set<string>(),
      poolID: preFill.poolID ?? null,
      permission: preFill.permission ?? RoutingFilterPermission.ALLOW,
      domainValidationStatus: DomainValidationStatus.NO_ERROR,
      errorMessage: '',
      projectsLoaded: false,
      treeIdToLuidMap: {},
      luidToTreeIdMap: {},
      luidToDisplayNameMap: {},
      conflictingProjectTreeIds: new Set<string>(),
      showMissingDomainWideWarning: conflict?.showMissingDomainWideWarning ?? false,
      hasDeletedProjects: conflict?.hasDeletedProjects ?? false,
    },
    actionDisabled: (_bodyProps, dialogState) => {
      return !dialogState.projectsLoaded
        || dialogState.domainValidationStatus !== DomainValidationStatus.NO_ERROR
        || dialogState.domainName.trim().length === 0
        || (dialogState.permission === RoutingFilterPermission.ALLOW && dialogState.poolID === null);
    },
    actionName: Messages.save(),
    action: async (_bodyProps, dialogState, updateDialogState) => {
      await submitCreateRoutingFilter(options, dialogState, updateDialogState);
    },
    isActionDelete: false,
    hideCancelButton: false,
    dialogWidth: TabDialogWidth.width375px,
    dismissDialogOnActionFailed: false,
  });
}
